import { useEffect, useMemo, useState } from "react";
import { CartDock } from "./components/CartDock";
import { CheckoutModal } from "./components/CheckoutModal";
import { CursorTooltip, type CursorTooltipState } from "./components/CursorTooltip";
import { ScrollMashup } from "./components/ScrollMashup";
import { Title3D } from "./components/Title3D";
import { saveCart, toggleCartItem, type CartState } from "./lib/cart";
import { loadGroceryItems } from "./lib/items";

// All personality types for preloading
const PERSONALITY_TYPES = [
  "farmers-market-poet",
  "aesthetic-nourisher",
  "balanced-overthinker",
  "caffeinated-visionary",
  "chaos-chef",
  "chill-gut-guru",
  "cozy-carb-collector",
  "culinary-maximalist",
  "cultural-collector",
  "fridge-curator",
  "green-alchemist",
  "indulgent-existenilist",
  "label-philosopher",
  "minimalist-forager",
  "snack-theorist",
  "time-traveler",
];

// Preload all grocery images on app start
function preloadImages(items: { imageUrl: string }[]) {
  items.forEach((item) => {
    const img = new Image();
    img.src = item.imageUrl;
  });

  // Also preload personality card images
  PERSONALITY_TYPES.forEach((type) => {
    const img = new Image();
    img.src = `${process.env.PUBLIC_URL || ""}/personality-cards/${type}.jpeg`;
  });
}

export function App() {
  const items = useMemo(() => loadGroceryItems(), []);

  // Preload all images once on mount
  useEffect(() => {
    preloadImages(items);
  }, [items]);
  const itemsById = useMemo(() => new Map(items.map((it) => [it.id, it])), [items]);

  const [cart, setCart] = useState<CartState>(() => ({}));
  const [tooltip, setTooltip] = useState<CursorTooltipState>({ open: false, text: "", x: 0, y: 0 });

  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const openCheckout = () => {
    setTooltip({ open: false, text: "", x: 0, y: 0 });
    setCheckoutOpen(true);
  };

  // If localStorage contains stale IDs (items removed/renamed), prune them so the UI and limits behave correctly.
  useEffect(() => {
    setCart((c) => {
      let changed = false;
      const next: CartState = {};
      for (const [id, qty] of Object.entries(c)) {
        if (typeof qty === "number" && Number.isFinite(qty) && qty > 0 && itemsById.has(id)) {
          next[id] = qty;
        } else {
          changed = true;
        }
      }
      return changed ? next : c;
    });
  }, [itemsById]);

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  const cartDisplayCount = useMemo(
    () => Object.keys(cart).filter((id) => (cart[id] ?? 0) > 0 && itemsById.has(id)).length,
    [cart, itemsById]
  );

  const cartEmpty = cartDisplayCount === 0;
  const remainingToCheckout = Math.max(0, 10 - cartDisplayCount);
  const canCheckout = cartDisplayCount >= 10;
  const remainingText = `${remainingToCheckout}`;
  const instructionText =
    cartDisplayCount === 0
      ? "Click an item to add to cart"
      : !canCheckout
        ? `Add ${remainingText} more ${remainingToCheckout === 1 ? "item" : "items"}`
        : "";

  return (
    <div className="appShell">
      <Title3D onTooltip={setTooltip} />
      <main className="content">
        <ScrollMashup
          items={items}
          cart={cart}
          onToggle={(id) => setCart((c) => toggleCartItem(c, id))}
          onTooltip={setTooltip}
        />
      </main>

      <div className="cartDockWrap cartDockShow" aria-hidden={false}>
        <div className="cartDockStack">
          <div
            className={`instructionPill ${cartEmpty ? "instructionPillEmpty" : ""} ${
              instructionText ? "instructionPillShow" : "instructionPillHide"
            }`}
          >
            <span key={instructionText} className="instructionText">
              {instructionText}
            </span>
          </div>

          {canCheckout ? (
            <div className="checkoutHitbox" onClick={openCheckout}>
              <button className="checkoutBtn" type="button">
                Checkout
              </button>
            </div>
          ) : null}

          <div className={`cartDockReveal ${cartEmpty ? "cartDockRevealHide" : "cartDockRevealShow"}`}>
            <CartDock cart={cart} itemsById={itemsById} onRemove={(id) => setCart((c) => toggleCartItem(c, id))} />
          </div>
        </div>
      </div>

      <div
        className="bottomFade"
        aria-hidden="true"
      />
      <CheckoutModal
        open={checkoutOpen}
        cart={cart}
        itemsById={itemsById}
        onClose={() => setCheckoutOpen(false)}
      />
      <CursorTooltip state={tooltip} />
    </div>
  );
}

export default App;
